import { AxiosInstance } from 'axios';

import { apiConfig, client } from '@/api';
import { dataReviewApi } from '@/api/clients/dataReviewApi';
import { DataReviewApi } from '@/api/generated/MNT/apis/data-review-api';
import { ApiRegion, config } from '@/config';

import { buildRegionalUrl, type RegionalRequestOpts } from './regionalFetch';
import { createRegionalHttp } from './regionalHttp';

/**
 * Clients the queue shell needs. The food, meal and staff clients are a separate chunk
 * (`regionalApis.ts`), loaded when a page actually calls them.
 */
export type CoreRegionalApis = {
  region: ApiRegion;
  /** Host without the `/api` suffix, e.g. `https://api.rxfoodapp.com`. */
  apiUrl: string;
  axios: AxiosInstance;
  dataReviewApi: DataReviewApi;
  /** Authenticated fetch of a path such as `/api/meal_photos/1/photo` on this region's host. */
  fetch: (path: string, opts?: RegionalRequestOpts) => Promise<Response>;
};

/** Canada keeps the shared clients, so Canada auth and 401 handling stay exactly as they are. */
export const canadaCoreApis: CoreRegionalApis = {
  region: ApiRegion.CA,
  apiUrl: config.API_URL,
  axios: client,
  dataReviewApi,
  fetch: (path, opts = {}) => {
    // No timeout on purpose: the Canada raw fetches this replaces had none.
    const { params, headers, timeoutMs: _timeoutMs, ...init } = opts;
    return fetch(buildRegionalUrl(ApiRegion.CA, path, params), {
      ...init,
      headers: { ...headers, Authorization: `Bearer ${apiConfig.accessToken}` },
    });
  },
};

export const createCoreRegionalApis = (region: ApiRegion, canadaAccessToken: string): CoreRegionalApis => {
  if (region === ApiRegion.CA) {
    return canadaCoreApis;
  }
  const http = createRegionalHttp(region, canadaAccessToken);
  return {
    region: http.region,
    apiUrl: http.apiUrl,
    axios: http.axios,
    dataReviewApi: new DataReviewApi(http.configuration, undefined, http.axios),
    fetch: http.fetch,
  };
};
